import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { VehicleType } from '../../../vehicle/params/vehicle-type';
import { PageFormSectionComponent } from '../../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { MobilizationSalaryRule } from '../../models/mobilization.model';
import { MobilizationService } from '../../mobilization.service';

@Component({
    selector: 'app-salary-rule-form',
    standalone: true,
    templateUrl: './salary-rule-form.component.html',
    styleUrls: ['../salary-rule-page.css'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputNumberModule,
        InputTextModule,
        SelectModule,
        TextareaModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ]
})
export class SalaryRuleFormComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    form!: FormGroup;
    salaryRuleId?: number;
    existing?: MobilizationSalaryRule;
    vehicleTypes: VehicleType[] = [];
    isLoading = false;
    isSaving = false;
    readonly activeOptions = [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ];

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private mobilizationService: MobilizationService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.salaryRuleId = Number(this.route.snapshot.paramMap.get('id')) || undefined;
        this.vehicleTypes = this.sessionStorageService.getVehicleTypes() ?? [];
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Mobilization', routerLink: '/fleet/mobilization' },
            { label: 'Salary Rules', routerLink: '/fleet/mobilization/salary-rules' },
            { label: this.salaryRuleId ? 'Edit' : 'Create' }
        ];
        this.form = this.fb.group({
            vehicleType: [null, Validators.required],
            dailyRate: [0, Validators.required],
            currencyCode: ['SAR', Validators.required],
            effectiveFrom: [null],
            effectiveTo: [null],
            priority: [0],
            active: [true, Validators.required],
            notes: ['']
        });

        if (this.salaryRuleId) {
            this.loadRule(this.salaryRuleId);
        }
    }

    save(): void {
        if (this.form.invalid || !this.validDateRange()) {
            this.form.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Missing details', detail: 'Truck type, daily rate, currency, and valid dates are required.' });
            return;
        }

        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        const vehicleType = this.form.value.vehicleType as VehicleType;
        this.isSaving = true;
        const payload: MobilizationSalaryRule = {
            ...this.existing,
            salaryRuleId: this.salaryRuleId,
            owningCompanyId: companyId,
            vehicleTypeId: vehicleType.id,
            vehicleTypeName: vehicleType.name,
            dailyRate: this.form.value.dailyRate,
            currencyCode: (this.form.value.currencyCode || 'SAR').toString().trim().toUpperCase(),
            effectiveFrom: this.form.value.effectiveFrom,
            effectiveTo: this.form.value.effectiveTo,
            priority: this.form.value.priority ?? 0,
            active: this.form.value.active,
            notes: this.form.value.notes
        };
        const request = this.salaryRuleId
            ? this.mobilizationService.updateSalaryRule(this.salaryRuleId, payload)
            : this.mobilizationService.createSalaryRule(payload);

        request.subscribe({
            next: response => {
                this.isSaving = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Salary rule saved.' });
                this.router.navigate(['/fleet/mobilization/salary-rules', response.data.salaryRuleId]);
            },
            error: error => {
                this.isSaving = false;
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || 'Could not save salary rule.' });
            }
        });
    }

    cancel(): void {
        if (this.salaryRuleId) {
            this.router.navigate(['/fleet/mobilization/salary-rules', this.salaryRuleId]);
            return;
        }
        this.router.navigate(['/fleet/mobilization/salary-rules']);
    }

    title(): string {
        return this.salaryRuleId ? 'Edit Salary Rule' : 'New Salary Rule';
    }

    selectedVehicleTypeName(): string {
        const vehicleType = this.form?.value.vehicleType as VehicleType | null;
        return vehicleType?.name || this.existing?.vehicleTypeName || '-';
    }

    private loadRule(id: number): void {
        this.isLoading = true;
        this.mobilizationService.getSalaryRule(id).subscribe({
            next: response => {
                this.existing = response.data;
                this.form.patchValue({
                    vehicleType: this.vehicleTypes.find(item => item.id === response.data.vehicleTypeId) ?? null,
                    dailyRate: response.data.dailyRate ?? 0,
                    currencyCode: response.data.currencyCode ?? 'SAR',
                    effectiveFrom: response.data.effectiveFrom ? new Date(response.data.effectiveFrom) : null,
                    effectiveTo: response.data.effectiveTo ? new Date(response.data.effectiveTo) : null,
                    priority: response.data.priority ?? 0,
                    active: response.data.active !== false,
                    notes: response.data.notes ?? ''
                });
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load salary rule.' });
            }
        });
    }

    private validDateRange(): boolean {
        const from = this.form.value.effectiveFrom ? new Date(this.form.value.effectiveFrom).getTime() : null;
        const to = this.form.value.effectiveTo ? new Date(this.form.value.effectiveTo).getTime() : null;
        return !from || !to || to >= from;
    }
}
